import { z } from 'zod';
import { ObjectId } from 'bson';
import { idSchema, nameSchema, objectIdSchema } from '../validation';
import { refSchema } from './ref.schema';
import { targetSchema, type Target } from './target.schema';

// `targetSchema` lives in its own file to break a circular import.
// Re-export here so consumers can keep importing every party concept
// from `party.schema` / `@hero/schema`.
export { targetSchema, type Target };

/**
 * Post Auth-v2 / relations-v2 hard cutover, `partyTargetSchema` is
 * strictly the 7-value {@link targetSchema}. The legacy 12-value
 * union that tolerated pre-cutover `relationSchema.target` values
 * (`business_customer`, `carrier_company_driver`, …) is gone —
 * green-field collections don't carry those values any more. The
 * name is retained so callers importing `partyTargetSchema` /
 * `PartyTarget` from `@hero/schema` keep compiling; the underlying
 * type IS {@link Target}.
 */
export const partyTargetSchema = targetSchema;
export type PartyTarget = Target;

/**
 * Payment / invoice dispatch key. Payment flows fan out to different
 * beneficiary collections (Org, User, Driver) depending on this target
 * value. Historically it drifted from `Relation.target` — for the
 * beneficiary code path we want `'carrier_company_driver'` /
 * `'freelancer_driver'` (both point at `Driver`) as first-class
 * distinguishable buckets, so the invoice dispatch table stays
 * expressive.
 *
 * Green-field: only these values are ever written now; anything else is
 * a bug. Callers that need the canonical {@link Target} should pipe
 * through {@link normalizeTarget}.
 */
export type UserBeneficiaryTarget =
  | Target
  | 'business_customer'
  | 'carrier_company'
  | 'individual_customer'
  | 'carrier_company_driver'
  | 'freelancer_driver'
  | 'super_admin_system';

/**
 * The `{_id, target}` handle that payment / invoice services use to
 * fetch the beneficiary doc from the right collection.
 *
 * Post relations-v2, `PartyResolverService.resolve({type: targetToIdType(target), id: _id.toHexString()})`
 * is the canonical way to dereference this handle.
 */
export type UserBeneficiary = {
  _id: ObjectId;
  target: UserBeneficiaryTarget;
};

/**
 * {@link UserBeneficiary} carrying the beneficiary's display name too,
 * for callsites that render the beneficiary directly (invoice PDFs,
 * payment receipts, notifications).
 */
export type UserBeneficiaryCommon = UserBeneficiary & { name: string };

/**
 * Legacy alias — equivalent to {@link Party} after the Auth-v2
 * hard cutover. Kept for a graceful sunset of `Relation` type imports
 * across the backend; new code should use {@link Party} directly.
 */
export type Relation = Party;

/**
 * The NETWORK ROLE a party plays. Multi-valued — a single Org can be
 * `business_customer + carrier_company + broker` simultaneously.
 *
 * `roles[0]` is the convention for "primary role for display".
 *
 * NOTE — Hero staff / system / super-admin actors are NOT party roles.
 * They appear as the document's `creator` snapshot instead.
 *
 * - `business_customer`   — a registered business that buys transport
 * - `carrier_company`     — a registered carrier that operates vehicles
 * - `broker`              — buys to resell capacity
 * - `individual_customer` — a single-person customer (not a registered business)
 * - `driver`              — a person assigned to vehicles
 * - `contact`             — a Contact entity within a Client's CRM
 */
export const partyRoleSchema = z.enum([
  'business_customer',
  'carrier_company',
  'broker',
  'individual_customer',
  'driver',
  'contact',
]);
export type PartyRole = z.infer<typeof partyRoleSchema>;

/**
 * CRM-selectable roles when creating or editing a {@link Client}.
 * Excludes system-managed roles (`driver`, `contact`).
 */
export const clientRoleSchema = partyRoleSchema.extract([
  'business_customer',
  'carrier_company',
  'broker',
  'individual_customer',
]);
export type ClientSelectableRole = z.infer<typeof clientRoleSchema>;

/**
 * The kind of identifier a `{type, id}` entry carries. Shared by
 * `tenantSchema` (visibility entries on documents) and `partySchema.ids[]`
 * (per-Party identifier-equivalents).
 *
 * snake_case values to match the dominant codebase convention.
 *
 * ObjectId-referencing types (`org_id`, `user_id`, `client_id`,
 * `profile_id`, `driver_id`, `contact_id`, `connection_id`) store the
 * source doc's `_id.toHexString()`. They are NOT native ObjectId at this
 * layer.
 *
 * Each id-type points at exactly ONE collection. There is no fan-out at
 * the schema layer — the resolver does single-collection probes per type.
 *
 * - `org_id`        — `Org` collection ref (the legacy `Business._id` and `Carrier._id` are reused as `Org._id` during the dual-write transition)
 * - `user_id`       — `User` collection ref (any role; super-admin lives in User too)
 * - `client_id`     — `Client` collection ref
 * - `profile_id`    — `Profile` collection ref ONLY. Global admin-curated
 *                     brand directory. Driver / Contact docs are NOT
 *                     `profile_id` — they have their own id-types below.
 * - `driver_id`     — `Driver` collection ref (per-Org driver record)
 * - `contact_id`    — `Contact` collection ref (per-Org / per-Client contact record)
 * - `connection_id` — `Connection` collection ref
 * - `phone`         — E.164 phone string (late-binding for unsigned contacts)
 * - `email`         — email string (late-binding for unsigned contacts)
 *
 * History — `profile_id` previously collapsed `Profile` / `Driver` /
 * `Contact` into a single id-type (ADR #9). That was reversed because
 * the conflation was actively misleading and produced a writer/reader
 * mismatch (`OrderService.getMyOrders` queried `'driver_id'` while the
 * writer stamped `'profile_id'`). See the new ADR in
 * `docs/relations-v2/01-decisions.md` and the back-compat notes on
 * `PartyResolverService.resolveSourceById`.
 */
export const idTypeSchema = z.enum([
  'org_id',
  'user_id',
  'client_id',
  'profile_id',
  'driver_id',
  'contact_id',
  'connection_id',
  'phone',
  'email',
]);
export type IdType = z.infer<typeof idTypeSchema>;

/**
 * A single identifier entry. Shape is uniform across `Party.ids[]` and
 * `tenantSchema` (visibility entries on documents).
 *
 * `id` is ALWAYS a string at this layer. For ObjectId-typed source `_id`
 * values, the string carries the 24-char hex form (`_id.toHexString()`).
 * For `phone` / `email`, the string is the natural value.
 *
 * See `docs/relations-v2/03-multi-tenants-structure.md` for the
 * rationale.
 */
export const idEntrySchema = z.object({
  type: idTypeSchema,
  /** ALWAYS a string. Hex form for ObjectId references; natural string for phone/email. */
  id: idSchema,
});
export type IdEntry = z.infer<typeof idEntrySchema>;

/**
 * Canonical snapshot of a referenced entity. Replaces {@link relationSchema}
 * for new writes. Lives on Order legs (`contracts[].buyer/seller`),
 * Agreement parties, Invoice payer/receiver, Vehicle owner, etc.
 *
 * Three new fields beyond the legacy `{_id, name, target, ref?}`:
 * - **`roles[]`** — the NETWORK ROLES this party plays. Multi-valued.
 *   `roles[0]` is the convention for "primary role". REQUIRED — every
 *   Party we materialize goes through {@link makeParty}, which both
 *   throws on empty roles AND sets `ids[0]` to the source `_id`. The
 *   schema mirrors that runtime invariant with `.min(1)` so any
 *   hand-rolled writer that forgets `roles[]` fails Zod parse instead
 *   of silently producing a half-built Party.
 * - **`ids[]`** — identifier-equivalents as `{type, id: string}`
 *   entries. ALWAYS contains the primary `_id` (hex string form) as
 *   the FIRST entry — that entry is auto-injected by
 *   {@link makeParty}, so `ids[]` is never empty in practice. Schema
 *   is `.min(1)` to enforce that invariant.
 * - **`target`** uses the tolerant {@link partyTargetSchema} union so
 *   legacy `relationTypesSchema` values still parse.
 *
 * See `docs/relations-v2/02-data-model.md` for the full data model and
 * `docs/relations-v2/03-multi-tenants-structure.md` for why this shape.
 */
export const partySchema = z.object({
  /** the source doc `_id`. Stays as native ObjectId. */
  _id: objectIdSchema,
  /** display name of the referenced entity (org / user / client / driver / contact). */
  name: nameSchema,
  /** which collection holds the source doc. Single value per snapshot. */
  target: partyTargetSchema,
  /**
   * network roles. Multi-valued. `roles[0]` is the convention for
   * primary-role display. ≥ 1 — enforced both at build time
   * ({@link makeParty}) and at the schema layer.
   */
  roles: z.array(partyRoleSchema).min(1),
  /**
   * identifier-equivalents. ALWAYS contains the primary `_id`
   * (hex string form) as the first entry — auto-injected by
   * {@link makeParty}. Additional identifiers (`phone`, `email`, …)
   * follow. ≥ 1 by construction, mirrored in the schema.
   */
  ids: z.array(idEntrySchema).min(1),
  /** any custom refs the user attached. Legacy `relationSchema.ref` preserved. */
  ref: refSchema.optional(),
});
export type Party = z.infer<typeof partySchema>;

/**
 * Map a new 7-value target to the matching `idTypeSchema` value used
 * when materializing `Party.ids[0]` (the primary-id duplication invariant
 * in {@link makeParty}).
 *
 * - `org`         → `org_id`
 * - `user`        → `user_id`
 * - `client`      → `client_id`
 * - `profile`     → `profile_id`  (Profile collection — global brand directory)
 * - `driver`      → `driver_id`   (Driver collection)
 * - `contact`     → `contact_id`  (Contact collection)
 * - `super_admin` → `user_id`     (super-admin entries live in User/AdminUser)
 *
 * Each target has its own id-type — see {@link idTypeSchema}. The
 * resolver ({@link PartyResolverService.resolveSourceById}) does
 * single-collection probes per type.
 */
export function targetToIdType(target: Target): IdType {
  switch (target) {
    case 'org':
      return 'org_id';
    case 'user':
      return 'user_id';
    case 'client':
      return 'client_id';
    case 'profile':
      return 'profile_id';
    case 'driver':
      return 'driver_id';
    case 'contact':
      return 'contact_id';
    case 'super_admin':
      return 'user_id';
  }
}

/**
 * Post Auth-v2 / relations-v2 hard cutover, every persisted
 * `Party.target` and `IdEntry` is already one of the 7 canonical
 * {@link Target} values. `normalizeTarget` is a pass-through kept as
 * a stable import name so callers (payment / invoice dispatch tables
 * that key on `beneficiary.target`) don't churn on the schema swap.
 *
 * Also accepts the `UserBeneficiary['target']` extras (`'broker'`,
 * `'carrier_company_driver'`, …) that some callsites still branch on
 * — those extras collapse into the matching canonical target so a
 * caller can safely feed a raw `beneficiary.target` and get a value
 * that switches cleanly on `Target`.
 */
export function normalizeTarget(raw: string): Target {
  switch (raw) {
    case 'business_customer':
    case 'carrier_company':
    case 'broker':
      return 'org';
    case 'individual_customer':
    case 'carrier_company_user':
    case 'business_customer_user':
    case 'super_admin_employee':
      return 'user';
    case 'third_party_customer':
      return 'client';
    case 'carrier_company_driver':
    case 'freelancer_driver':
      return 'driver';
    case 'super_admin_system':
      return 'super_admin';
    default:
      return raw as Target;
  }
}

/**
 * Build an {@link IdEntry} for the primary `_id` of a source doc, given
 * its {@link Target}. Helper for {@link makeParty} and ad-hoc callsites.
 */
export function makePrimaryIdEntry(
  source: { _id: { toHexString: () => string } },
  target: Target,
): IdEntry {
  return {
    type: targetToIdType(target),
    id: source._id.toHexString(),
  };
}

/**
 * Build the {@link Party} for a field that points at a `Driver` row
 * specifically — `Order.driver`, `Vehicle.driver`, an assignment.
 *
 * **This is the narrow case.** When you are linking a *person* — who owns
 * this install, who filed this document, who pays for this — use
 * {@link makeDriverUserParty} instead. See its docs for why.
 *
 * `extraIds` is for identifiers that are genuinely known at the call site —
 * usually the login behind the driver, when the caller has it from a token.
 */
export function makeDriverParty(
  driver: { _id: { toHexString: () => string }; name: string },
  extraIds: IdEntry[] = [],
): Party {
  return {
    _id: driver._id as Party['_id'],
    name: driver.name,
    target: 'driver',
    roles: ['driver'],
    ids: [makePrimaryIdEntry(driver, 'driver'), ...extraIds],
  };
}

/**
 * Build the {@link Party} for a person who happens to drive.
 *
 * **The default for linking people.** A human has exactly one login and
 * may hold any number of role-specific rows — a `Driver` today, a second
 * one under a fleet tomorrow — so the login is the only identifier that
 * names *them* rather than one of their hats. Keying on `Driver._id`
 * instead splits one person across collections: their subscription lands
 * on the `User._id` (that is where money lives) while their install and
 * audit trail land on the `Driver._id`, and then "does this person have
 * Logisti?" and "has this person paid?" are questions about two different
 * ids that no index can join.
 *
 * So `_id` is the login and `driver_id` rides in `ids[]`, which is what
 * `ids[]` is for. `target: 'user'` with `roles: ['driver']` is the shape
 * {@link isFreelancerOwnerParty} already recognizes as an owner-operator,
 * and {@link subscriptionOwnerRefOf} resolves without a lookup.
 *
 * Reserve {@link makeDriverParty} for the fields that genuinely point at
 * the Driver record itself.
 */
export function makeDriverUserParty(
  driver: {
    _id: { toHexString: () => string };
    name: string;
    userId: { toHexString: () => string } | string;
  },
  extraIds: IdEntry[] = [],
): Party {
  const userId = typeof driver.userId === 'string' ? driver.userId : driver.userId.toHexString();
  return {
    _id: new ObjectId(userId) as Party['_id'],
    name: driver.name,
    target: 'user',
    roles: ['driver'],
    ids: [
      makeIdEntry('user_id', userId),
      makeIdEntry('driver_id', driver._id),
      ...extraIds.filter(entry => entry.type !== 'user_id' && entry.type !== 'driver_id'),
    ],
  };
}

/**
 * Build an {@link IdEntry} for a known {@link IdType} + id value.
 *
 * Use this when you already know the `IdType` (e.g. when appending an
 * `extraIds` entry for a bridged identifier — `profile_id` from
 * `Client.linkedParty` when `target === 'profile'`, `org_id` from
 * `Profile.linkedOrgId`, etc.). When you have a {@link Target}
 * instead, prefer {@link makePrimaryIdEntry} so the type is derived from
 * the canonical {@link targetToIdType} switch.
 *
 * Accepts either a hex string or an ObjectId-like value (anything with
 * `toHexString()`). The output id is ALWAYS a hex string at this layer.
 */
export function makeIdEntry(
  type: IdType,
  id: string | { toHexString: () => string },
): IdEntry {
  return {
    type,
    id: typeof id === 'string' ? id : id.toHexString(),
  };
}

/**
 * Reverse of {@link targetToIdType}. Given an {@link IdType}, return the
 * list of {@link Target} values it can resolve to.
 *
 * Almost one-to-one: each id-type points at a single collection, so the
 * return is a single-element `readonly` tuple. The exception is
 * `user_id`, which covers `user` (any role) plus `super_admin` (AdminUser
 * collection is reached via the same idType — see
 * `PartyResolverService.resolveSourceById`).
 *
 * Returns `readonly []` for the late-binding identifier types
 * (`phone` / `email`) — those do NOT resolve directly to a single
 * collection (see `docs/relations-v2/04-signup-linking-design.md`).
 *
 * History — `profile_id` previously fanned out to
 * `['profile', 'driver', 'contact']`. ADR #9 was reversed; `profile_id`
 * is now Profile-only. The resolver keeps a back-compat shim for
 * pre-split rows that haven't been rewritten by a writer yet, but that
 * shim is INTERNAL to the resolver and does not leak through this map.
 */
export function idTypeToTargets(type: IdType): readonly Target[] {
  switch (type) {
    case 'org_id':
      return ['org'] as const;
    case 'user_id':
      return ['user', 'super_admin'] as const;
    case 'client_id':
      return ['client'] as const;
    case 'profile_id':
      return ['profile'] as const;
    case 'driver_id':
      return ['driver'] as const;
    case 'contact_id':
      return ['contact'] as const;
    case 'connection_id':
    case 'phone':
    case 'email':
      return [] as const;
  }
}

/**
 * Map a {@link Target} to the canonical MongoDB collection name that
 * holds its source documents.
 *
 * Single source of truth for "where does a Party of this `target` live".
 * Mirrors the dispatch table in `PartyResolverService.resolveSourceById`
 * (which goes from `IdType` -> collection via {@link idTypeToTargets}).
 *
 * - `org`         -> `Org`
 * - `user`        -> `User`
 * - `client`      -> `Client`
 * - `profile`     -> `Profile`     (global brand directory)
 * - `driver`      -> `Driver`      (per-org driver records)
 * - `contact`     -> `Contact`     (per-org CRM contacts)
 * - `super_admin` -> `AdminUser`   (Hero staff)
 */
export function targetToCollection(target: Target): string {
  switch (target) {
    case 'org':
      return 'Org';
    case 'user':
      return 'User';
    case 'client':
      return 'Client';
    case 'profile':
      return 'Profile';
    case 'driver':
      return 'Driver';
    case 'contact':
      return 'Contact';
    case 'super_admin':
      return 'AdminUser';
  }
}
